/* eslint-disable max-lines */

import React, { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getCommentError } from 'selectors/envelopeReview';
import { useSelector } from 'store';
import logEvent from 'utils/analytics';

import { InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';

import { $generateHtmlFromNodes } from '@lexical/html';
import { $getRoot } from 'lexical';
import { getUsers } from 'selectors/users';
import { CommentWithUsers } from 'types';
import ClearEditorPlugin from 'utils/lexical/ClearEditorPlugin';
import ExecuteOnEnterPlugin from 'utils/lexical/ExecuteOnEnterPlugin';
import AutofocusPlugin from 'utils/lexical/ExecuteOnEnterPlugin copy';
import LoadHtmlPlugin from 'utils/lexical/LoadHtmlPlugin';
import HandleModifyMentionsPlugin from 'utils/lexical/Mentions/HandleModifyMentionsPlugin';
import { MentionNode } from 'utils/lexical/Mentions/MentionNode';
import MentionsPlugin from 'utils/lexical/Mentions/MentionsPlugin';

type ComponentProps = {
  setComment: (comment: CommentWithUsers) => void;
  comment: CommentWithUsers;
  handleSaveComment: (value?: string) => void;
  onCancelComment?: () => void;
  scrollOnError?: boolean;
  isEditing?: boolean;
  focusOnMount?: boolean;
};

const initialConfig: InitialConfigType = {
  namespace: 'comment-section-editor',
  nodes: [MentionNode],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onError: (error: any): void => {
    throw error;
  },
};

const AddCommentSection: React.FC<ComponentProps> = ({
  setComment,
  comment,
  onCancelComment,
  handleSaveComment,
  scrollOnError = false,
  isEditing = false,
  focusOnMount = false,
}) => {
  const { envelopeId } = useParams<{ envelopeId: string }>();

  const clearRef = useRef<React.ElementRef<typeof ClearEditorPlugin>>(null);

  const commentError = useSelector(getCommentError);
  const commentsRef = useRef<HTMLDivElement>(null);
  const users = useSelector(getUsers);

  useEffect(() => {
    if (commentError && scrollOnError) {
      commentsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [commentError, scrollOnError]);

  useEffect(() => {
    clearRef.current?.clear();
  }, [envelopeId]);

  const saveComment = (value?: string): void => {
    logEvent('envelope-add-comment');

    const val = value || comment.value;
    if (!val.trim() || isEditing) {
      return;
    }

    handleSaveComment(val);

    clearRef.current?.clear();
    setComment({ value: '', text: '', taggedUsers: [] });
  };

  const handleCancelComment = (): void => {
    clearRef.current?.clear();
    setComment({ value: '', text: '', taggedUsers: [] });
    if (onCancelComment) {
      onCancelComment();
    }
  };

  const handleCreateMention = (name: string, email: string, val: string): void => {
    setComment({
      value: comment.value,
      text: comment.text,
      taggedUsers: [
        ...comment.taggedUsers.filter((u) => u.uuid !== val),
        {
          name,
          email,
          uuid: val,
        },
      ],
    });
  };

  const handleDeleteMention = (val: string): void => {
    setComment({
      value: comment.value,
      text: comment.text,
      taggedUsers: comment.taggedUsers.filter((u) => u.uuid !== val),
    });
  };

  return (
    <div ref={commentsRef} className="flex flex-col gap-2 pl-3 pr-4">
      <div className="relative">
        <LexicalComposer initialConfig={initialConfig}>
          <LoadHtmlPlugin content="" />

          <MentionsPlugin
            initialOptions={Object.values(users).map((user) => ({
              name: user.name || '',
              email: user.email || '',
              value: user.uuid || '',
            }))}
          />
          <ClearEditorPlugin ref={clearRef} />
          <HandleModifyMentionsPlugin
            createdCallback={handleCreateMention}
            destroyedCallback={handleDeleteMention}
          />
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                aria-placeholder="Hit 'Enter' to save"
                placeholder={
                  <div className="absolute italic" style={{ bottom: 4, right: 8 }}>
                    Hit &apos;Enter&apos; to save
                  </div>
                }
                className="form-input min-h-20 max-h-20 p-2 text-body rounded border border-litlingo-gray-2 overflow-auto custom-scrollbar"
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          {focusOnMount && <AutofocusPlugin />}
          <OnChangePlugin
            onChange={(_editorState, editor): void => {
              editor.update(() => {
                const htmlString = $generateHtmlFromNodes(editor);
                const stringifiedEditorState = JSON.stringify(editor.getEditorState().toJSON());
                const parsedEditorState = editor.parseEditorState(stringifiedEditorState);
                const textContent = parsedEditorState.read(() => $getRoot().getTextContent());

                setComment({
                  value: htmlString,
                  text: textContent,
                  taggedUsers: comment.taggedUsers,
                });
              });
            }}
          />
          <ExecuteOnEnterPlugin callback={saveComment} />
        </LexicalComposer>
      </div>
      {commentError && (
        <div className="flex flex-row gap-1 items-center text-body">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8 0C5.87827 0 3.84344 0.842855 2.34315 2.34315C0.842861 3.84344 0 5.87827 0 8C0 10.1217 0.842861 12.1566 2.34315 13.6569C3.84344 15.1571 5.87827 16 8 16C10.1216 15.9996 12.1562 15.1567 13.6564 13.6564C15.1567 12.1562 15.9996 10.1216 16 8C16 5.87827 15.1571 3.84344 13.6569 2.34315C12.1566 0.842855 10.1217 0 8 0ZM11.1743 10.578C11.2529 10.6568 11.297 10.7635 11.297 10.8747C11.297 10.986 11.2529 11.0927 11.1743 11.1714C11.0956 11.2502 10.9889 11.2945 10.8775 11.2946C10.7662 11.2945 10.6595 11.2502 10.5808 11.1714L8.0056 8.59622L5.42898 11.1714C5.35031 11.2502 5.24359 11.2945 5.13227 11.2946C5.02095 11.2945 4.91422 11.2502 4.83556 11.1714C4.75693 11.0927 4.71276 10.986 4.71276 10.8747C4.71276 10.7635 4.75693 10.6568 4.83556 10.578L7.41218 8.0028L4.83556 5.42617C4.75693 5.34745 4.71276 5.24073 4.71276 5.12946C4.71276 5.0182 4.75693 4.91148 4.83556 4.83275C4.91428 4.75412 5.021 4.70996 5.13227 4.70996C5.24353 4.70996 5.35025 4.75412 5.42898 4.83275L8.0056 7.40938L10.5808 4.83275C10.6596 4.75412 10.7663 4.70996 10.8775 4.70996C10.9888 4.70996 11.0955 4.75412 11.1743 4.83275C11.2529 4.91148 11.297 5.0182 11.297 5.12946C11.297 5.24073 11.2529 5.34745 11.1743 5.42617L8.59902 8.0028L11.1743 10.578Z"
              fill="#F8351A"
            />
          </svg>
          <span>An escalated status requires a comment</span>
        </div>
      )}
      <div className="flex flex-row justify-end gap-1">
        <button
          type="button"
          onClick={handleCancelComment}
          className="h-6 button button--secondary flex justify-center"
        >
          <span className="font-bold">Cancel</span>
        </button>
        <button
          type="button"
          onClick={(): void => saveComment()}
          className="h-6 button button--primary"
          disabled={isEditing}
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default AddCommentSection;
